import { toast } from "@/components/ui/use-toast";
import { useParseSkuFileMutation, useValidateSkusMutation } from "@/services/campaign";
import { useCallback, useRef, useState } from "react";

interface ValidItem {
  sku: string;
  name: string;
}

interface PreviewData {
  validItems: ValidItem[];
  invalidSkus: string[];
  duplicateSkus: string[];
}

const HARD_LIMIT = 15000;
const SOFT_LIMIT = 10000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Normaliza SKU para comparação resiliente a whitespace/case.
// Oracle CHAR columns retornam com padding; Excel pode vir com espaço.
// Sem isso, um SKU "104007 " do DB não bate com "104007" do Excel
// e o preview mostra como "válido" em vez de "duplicado".
const normalizeSku = (sku: string): string => String(sku).trim().toUpperCase();

export function useExcelImport(currentItems: Array<{ sku: string; name?: string }>) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);

  const [parseSkuFile, { isLoading: isParsing }] = useParseSkuFileMutation();
  const [validateSkus, { isLoading: isValidating }] = useValidateSkusMutation();

  const isProcessing = isParsing || isValidating || uploadProgress !== null;

  const clearFileInput = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
        toast({
          title: "Arquivo inválido",
          description: "Por favor, envie um arquivo Excel (.xlsx)",
          variant: "destructive",
        });
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "Arquivo muito grande",
          description:
            "O arquivo não pode exceder 10MB. Tente reduzir o número de linhas.",
          variant: "destructive",
        });
        return;
      }

      setUploadProgress(0);

      try {
        const result = await parseSkuFile(file).unwrap();

        if (result.skus.length > HARD_LIMIT) {
          toast({
            title: "Limite excedido",
            description: `Máximo de ${HARD_LIMIT.toLocaleString()} itens permitido. O arquivo contém ${result.skus.length.toLocaleString()} itens.`,
            variant: "destructive",
          });
          setUploadProgress(null);
          clearFileInput();
          return;
        }

        setUploadProgress(33);

        const skuCodes = result.skus.map((item) => item.sku);
        const validation = await validateSkus({ skus: skuCodes }).unwrap();

        setUploadProgress(66);

        const currentSkus = new Set(
          currentItems.map((item) => normalizeSku(item.sku)),
        );
        const duplicates = validation.valid
          .filter((item) => currentSkus.has(normalizeSku(item.sku)))
          .map((item) => item.sku);

        setUploadProgress(100);
        setTimeout(() => setUploadProgress(null), 500);

        setPreview({
          validItems: validation.valid,
          invalidSkus: validation.invalid,
          duplicateSkus: duplicates,
        });

        clearFileInput();
      } catch (error: unknown) {
        const errorMessage =
          error &&
          typeof error === "object" &&
          "data" in error &&
          error.data &&
          typeof error.data === "object" &&
          "message" in error.data
            ? String(error.data.message)
            : "Erro ao processar arquivo Excel";

        toast({
          title: "Erro ao importar",
          description: errorMessage,
          variant: "destructive",
        });

        setUploadProgress(null);
        clearFileInput();
      }
    },
    [parseSkuFile, validateSkus, currentItems, clearFileInput],
  );

  const closePreview = useCallback(() => {
    setPreview(null);
  }, []);

  const confirmImport = useCallback(
    async (
      items: Array<{ sku: string; name?: string }>,
      onAdd: (items: Array<{ sku: string; name?: string }>) => Promise<void>,
    ): Promise<void> => {
      if (items.length === 0) return;

      const totalAfterImport = currentItems.length + items.length;
      if (totalAfterImport > SOFT_LIMIT) {
        toast({
          title: "Aviso: Muitos itens",
          description: `${totalAfterImport.toLocaleString()} itens. Performance pode ser afetada acima de ${SOFT_LIMIT.toLocaleString()} itens.`,
          variant: "default",
        });
      }

      await onAdd(items);
    },
    [currentItems],
  );

  return {
    fileInputRef,
    uploadProgress,
    isProcessing,
    preview,
    handleFileUpload,
    closePreview,
    confirmImport,
  };
}
